import * as React from "react";
import { Box, Button, Grid, TextField, IconButton, Typography, MenuItem, InputAdornment } from "@mui/material";
import { DateTimeField } from "@mui/x-date-pickers";
import Loader from "../../components/general/loader";
import { Title } from "../general/typography";
import dayjs from "dayjs";
import "dayjs/locale/be";
import useBackend from "../../hooks/use_backend";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { REACT_QUERY_KEYS } from "../../enums/react_query_key_enum";
import HelpedSelect from "../../components/general/helped_select";
import {
  getOverrideModeLabels,
  getModeLabelByValue,
  getTypeLabelByValue,
  getModeValueByLabel,
  getOverrideTypeLabels,
  getTypeValueByLabel,
  OVERRIDE_MODES,
  OVERRIDE_TYPES,
  POS_ONLY_OVERRIDES,
  NO_TARGET_MODES,
  getFullOverrideName,
} from "../../enums/override_enum";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { ClearIcon } from "@mui/x-date-pickers";
import { useModal } from "../../context/modal_provider";
import { asKw, processValue, validateValue } from "../../scripts/utils";
import HelpedAdornedTextField from "../general/helped_adorned_text_field";
import explainOverride from "./explain_override";
import { useSnackbar } from "notistack";
import { addOverride, getValidOverrides } from "../../scripts/api_v2/overrides_client";
import { fetchSiteMaximumProduction } from "../../scripts/api_v2/site_client";

export function CreateOverride({ siteData }) {
  const backend = useBackend();
  const { enqueueSnackbar } = useSnackbar();
  const [validFromError, setValidFromError] = React.useState(null);
  const [validToError, setValidToError] = React.useState(null);
  const { closeOverrideModal } = useModal();
  const [period, setPeriod] = React.useState("For next 5 minutes");
  const periodOptions = [
    "For next 5 minutes",
    "Until end of quarter hour",
    "Until end of hour",
    "Until end of day",
    "Custom",
  ];
  const queryClient = useQueryClient();

  const fromErrorMessage = React.useMemo(() => {
    if (validFromError === "minDateTime" || validFromError === "minDate" || validFromError === "minTime") {
      return "A date in the past is invalid";
    } else if (validFromError === "invalidDate") {
      return "Invalid date";
    }
  }, [validFromError]);

  const toErrorMessage = React.useMemo(() => {
    if (validToError === "minDateTime" || validToError === "minDate" || validToError === "minTime") {
      return "Date must be later than 'valid from' date";
    } else if (validToError === "invalidDate") {
      return "Invalid date";
    }
  }, [validToError]);

  const {
    data: overrides,
    error,
    isLoading,
    refetch: refetchOverrides,
  } = useQuery({
    queryKey: [REACT_QUERY_KEYS.CURRENT_OVERRIDES, backend.fetch, siteData.site_id],
    queryFn: () => getValidOverrides(backend.fetch, siteData.site_id),
    enabled: false,
  });

  const { data: siteMaximumProduction } = useQuery({
    queryKey: [REACT_QUERY_KEYS.SITE_MAXIMUM_PRODUCTION, backend.fetch, siteData.site_id],
    queryFn: () => fetchSiteMaximumProduction(backend.fetch, siteData.site_id),
    enabled: !!siteData?.site_id,
  });

  const { mutate: commitOverride, isPending: isCommitting } = useMutation({
    mutationFn: async (override) => {
      await addOverride(backend.fetch, siteData.site_id, override);
    },
    onMutate: async (data) => {
      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({
        queryKey: [REACT_QUERY_KEYS.CURRENT_OVERRIDES, backend.fetch, siteData.site_id],
      });
      // Ideally we would optimistically update to the new value
    },
    onError: (err, variables, context) => {
      console.error(err);
      enqueueSnackbar("Override add failed", { variant: "error" });
    },
    onSuccess: async () => {
      await refetchOverrides();
      enqueueSnackbar("Override added!", { variant: "success" });
    },
  });

  const [newOverride, setNewOverride] = React.useState({
    mode: OVERRIDE_MODES.FLEX.value,
    type: OVERRIDE_TYPES.FRACTION.value,
    value: 100,
    valid_from: dayjs(),
    valid_to: undefined,
    device_id: undefined,
  });

  const boxSx = {
    width: "100%",
    borderRadius: 3,
    padding: 1,
    backgroundColor: "white",
    display: "flex",
    flexDirection: "column",
  };

  const overrideItemSx = { display: "flex", alignItems: "center", justifyContent: "left", px: 1, pt: 1 };

  const curtailmentProductionLimit = React.useMemo(() => {
    const fallback = Math.abs(siteData?.config?.max_grid_inject_sp ?? 0);
    if (siteMaximumProduction === null || siteMaximumProduction === undefined || isNaN(siteMaximumProduction)) {
      return fallback;
    }
    return Math.abs(siteMaximumProduction);
  }, [siteMaximumProduction, siteData?.config?.max_grid_inject_sp]);

  const getMaxDevicePowers = (device_id) => {
    const device = siteData?.devices?.[device_id];
    if (!device) return [0, 0];
    if (device.device_class === "battery") return [-device.charge_rate, device.charge_rate];
    else if (device.device_class === "solar_inverter") return [0, device.inverter_power];
    else if (device.device_class === "ev_charger") return [0, device.charge_power];
    else return [0, 0];
  };

  const getMaxPowers = () => {
    if (newOverride.device_id !== undefined) {
      return getMaxDevicePowers(newOverride.device_id);
    } else {
      if (newOverride.mode === OVERRIDE_MODES.FLEX.value) {
        return [-siteData.max_discharge_rate, siteData.max_charge_rate];
      }
      if (newOverride.mode === OVERRIDE_MODES.CURTAILMENT_PRODUCTION.value) {
        return [-curtailmentProductionLimit, curtailmentProductionLimit];
      }
      return [-siteData.config.max_grid_inject_sp, siteData.config.max_grid_offtake_sp];
    }
  };

  const [globalMaxInj, globalMaxOff] = getMaxPowers();

  const getValidTSForPeriod = (period, valid_from, valid_to) => {
    const now = dayjs();
    if (period === "For next 5 minutes") return [now.unix(), now.add(5, "minutes").unix()];
    else if (period === "Until end of quarter hour")
      return [
        now.unix(),
        now
          .minute(Math.ceil(now.minute() / 15) * 15)
          .second(0)
          .millisecond(0)
          .unix(),
      ];
    else if (period === "Until end of hour") return [now.unix(), now.endOf("hour").unix()];
    else if (period === "Until end of day") return [now.unix(), now.endOf("day").unix()];
    else if (period === "Custom") {
      if (valid_to === undefined) return [valid_from.unix(), null];
      else return [valid_from.unix(), valid_to.unix()];
    }
  };

  function overrideValidationError(val) {
    const [max_inj, max_off] = getMaxPowers();
    if (validateValue(val) === null || isNaN(asKw(val))) return true;
    if (newOverride.type === OVERRIDE_TYPES.FRACTION.value) {
      if (POS_ONLY_OVERRIDES.includes(newOverride.mode)) {
        return val < 0 || val > 100;
      }
      return val < -100 || val > 100;
    } else {
      if (POS_ONLY_OVERRIDES.includes(newOverride.mode)) {
        return val < 0 || val > max_off;
      }
      return val < max_inj || val > max_off;
    }
  }

  function overrideValidationText(val) {
    if (!overrideValidationError(val)) return null;

    if (validateValue(val) === null || isNaN(asKw(val))) return "Invalid value";
    const [max_inj, max_off] = getMaxPowers();
    if (newOverride.type === OVERRIDE_TYPES.FRACTION.value) {
      if (POS_ONLY_OVERRIDES.includes(newOverride.mode) && (val < 0 || val > 100)) {
        return `Outside range of 0% to 100%, only positive values make sense for this mode`;
      }
      return `Outside range of -100% to 100%`;
    } else {
      if (POS_ONLY_OVERRIDES.includes(newOverride.mode) && val < 0) {
        return `Outside range of 0kW to ${asKw(max_off)}kW, only positive values makes sense for this mode`;
      }
      return `Outside range of ${asKw(max_inj)}kW to ${asKw(max_off)}kW, the maximum ${newOverride.device_id !== undefined ? `device` : `Elion injection/offtake`} setpoints`;
    }
  }

  function overrideModeValidationError(val) {
    return !siteData.commissioning_completed && val == OVERRIDE_MODES.GRID.value;
  }

  function overrideModeValidationText(val) {
    if (!siteData.commissioning_completed && val == OVERRIDE_MODES.GRID.value) {
      return "Can't set a grid override during commissioning";
    }
  }

  function handleClear() {
    setNewOverride((prev) => {
      return { ...prev, device_id: undefined };
    });
  }

  async function handleAddOverride() {
    let overrideValue = validateValue(newOverride.value);
    if (overrideValue === null) {
      return enqueueSnackbar("Invalid override value", { variant: "error" });
    }
    if (newOverride.type === OVERRIDE_TYPES.FRACTION.value) {
      overrideValue = overrideValue / 100;
    }
    try {
      const overrideName = getFullOverrideName(newOverride.mode, newOverride.type);
      const [valid_from_ts, valid_to_ts] = getValidTSForPeriod(period, newOverride.valid_from, newOverride.valid_to);
      const override = {
        value: overrideValue,
        override: overrideName,
        valid_from_ts: valid_from_ts,
        valid_to_ts: valid_to_ts,
        device_id: newOverride.device_id,
      };
      await commitOverride(override);
    } catch (err) {
      return enqueueSnackbar(err, { variant: "error" });
    }
  }

  function deviceName(device_id) {
    if (!device_id) return "All";
    else if (!siteData.devices) return "Unknown";
    else if (!siteData.devices[device_id]) return "Unkown";
    else return siteData.devices[device_id].name;
  }

  function OverrideAdornment({ overrideType }) {
    return (
      <InputAdornment sx={{ ml: 1 }}>
        <Typography color="#BBB" fontSize={13} noWrap>
          {overrideType === OVERRIDE_TYPES.POWER.value ? "Watt" : "%"}
        </Typography>
      </InputAdornment>
    );
  }

  const devs = Object.keys(siteData.devices);

  return (
    <Box sx={boxSx}>
      <Title mb={2}>Create Override</Title>
      <Grid container pl={3} justifyContent="start" alignItems="start">
        <Grid md={3} xs={12} item sx={overrideItemSx}>
          <HelpedSelect
            options={getOverrideModeLabels()}
            label={"Mode"}
            key="mode"
            info="Select the override mode"
            selected={getModeLabelByValue(newOverride.mode)}
            onChange={(e) => {
              setNewOverride((prev) => {
                return { ...prev, mode: getModeValueByLabel(e.target.value), device_id: undefined };
              });
            }}
            error={overrideModeValidationError(newOverride.mode)}
          />
        </Grid>
        <Grid md={3} xs={12} item sx={overrideItemSx}>
          <HelpedSelect
            options={getOverrideTypeLabels()}
            label={"Type"}
            key="type"
            info="Power: value in Watt, Fraction: value in %"
            selected={getTypeLabelByValue(newOverride.type)}
            onChange={(e) => {
              setNewOverride((prev) => {
                return { ...prev, type: getTypeValueByLabel(e.target.value) };
              });
            }}
          />
        </Grid>
        <Grid md={6} xs={12} item sx={overrideItemSx}>
          {devs.length > 0 && (
            <TextField
              value={newOverride.device_id ? newOverride.device_id : ""}
              label={
                NO_TARGET_MODES.includes(newOverride.mode)
                  ? "Mode does not allow target device"
                  : "Target device (optional)"
              }
              select
              fullWidth
              InputProps={{
                endAdornment: newOverride.device_id && (
                  <InputAdornment position="end" sx={{ paddingRight: 2 }}>
                    <IconButton onClick={handleClear}>
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              onChange={(e) => {
                setNewOverride((prev) => {
                  return { ...prev, device_id: e.target.value };
                });
              }}
              disabled={NO_TARGET_MODES.includes(newOverride.mode)}
            >
              {" "}
              {Object.entries(siteData.devices).map(([key, value]) => {
                return (
                  <MenuItem key={key} value={key}>
                    [{key}] {value?.device_class} - {value?.name}
                  </MenuItem>
                );
              })}
            </TextField>
          )}
        </Grid>
      </Grid>
      <Grid container pl={3} justifyContent="start" alignItems="center" pt={3}>
        <Grid md={3} xs={12} item sx={overrideItemSx}>
          <HelpedAdornedTextField
            adornment={<OverrideAdornment overrideType={newOverride.type} />}
            key="value"
            info={
              newOverride.type === OVERRIDE_TYPES.fraction
                ? "Value between -100% and 100%"
                : `Value between ${globalMaxInj}W and ${globalMaxOff}W`
            }
            label={"Override value"}
            type="integer"
            fullWidth
            value={newOverride.value}
            error={overrideValidationError(newOverride.value)}
            onChange={(e) => {
              setNewOverride((prev) => {
                return { ...prev, value: processValue(e.target.value, true) };
              });
            }}
          />
        </Grid>
        <Grid md={9} xs={12} item sx={overrideItemSx}>
          {overrideModeValidationText(newOverride.mode) ? (
            <Typography color="error">{overrideModeValidationText(newOverride.mode)}</Typography>
          ) : (
            <Typography color="error">{overrideValidationText(newOverride.value)}</Typography>
          )}
        </Grid>
      </Grid>
      <Grid container pl={3} justifyContent="start" alignItems="start" pt={3}>
        <Grid md={3} xs={12} item sx={overrideItemSx}>
          <HelpedSelect
            options={periodOptions}
            label={"Period"}
            key="period"
            info="Select the period or define custom"
            disabled={!siteData.commissioning_completed}
            selected={period}
            onChange={(e) => {
              setNewOverride((prev) => {
                return { ...prev, valid_from: dayjs() };
              });
              setPeriod(e.target.value);
            }}
          />
        </Grid>
        {period === "Custom" && (
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={"be"}>
            <Grid md={3} xs={12} item sx={overrideItemSx}>
              <DateTimeField
                fullWidth
                minDateTime={dayjs().subtract(5, "minute")}
                onError={(newError) => {
                  setValidFromError(newError);
                }}
                helperText={fromErrorMessage}
                label="Valid from"
                value={newOverride.valid_from}
                onChange={(val) => {
                  setNewOverride((prev) => {
                    return { ...prev, valid_from: val };
                  });
                }}
                format="L HH:mm"
              />
            </Grid>
            <Grid md={3} xs={12} item sx={overrideItemSx}>
              <DateTimeField
                fullWidth
                onError={(newError) => setValidToError(newError)}
                minDateTime={newOverride.valid_from}
                value={newOverride.valid_to}
                helperText={toErrorMessage}
                onChange={(val) => {
                  setNewOverride((prev) => {
                    return { ...prev, valid_to: val };
                  });
                }}
                clearable
                label="Valid to"
                format="L HH:mm"
              />
            </Grid>
          </LocalizationProvider>
        )}
      </Grid>
      <Grid container pl={3} justifyContent="start" alignItems="start" pt={3}>
        <Grid md={12} xs={12} item sx={overrideItemSx}>
          <Typography style={{ fontWeight: "bold" }}>The override will:</Typography>
          <Typography style={{ paddingLeft: 15 }}>
            {explainOverride(newOverride, deviceName(newOverride.device_id), period, getMaxPowers())}
          </Typography>
        </Grid>
      </Grid>
      <Grid container justifyContent="center" mt={2}>
        <Box
          sx={{
            display: "flex",
            width: "80%",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "row",
            pt: 1,
          }}
        >
          <Button onClick={closeOverrideModal} sx={{ mr: 1 }}>
            Close overrides
          </Button>
          <Box sx={{ flex: "1 1 auto" }} />
          {isCommitting ? (
            <Box sx={{ alignSelf: "center" }}>
              <Loader fill={true} />
            </Box>
          ) : (
            <Button
              onClick={() => handleAddOverride()}
              disabled={
                overrideValidationError(newOverride.value) ||
                validFromError ||
                validToError ||
                overrideModeValidationError(newOverride.mode)
              }
              variant="contained"
            >
              Add override
            </Button>
          )}
        </Box>
      </Grid>
    </Box>
  );
}
